import React from "react";

import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { faPen } from "@fortawesome/pro-solid-svg-icons";
import axios from "axios";
import { Controller, useForm, useWatch } from "react-hook-form";

import { getModuleDisplayName } from "src/adminPanel/modules/ModulesSettings";
import {
  ModuleSpec,
  ModuleUpdateRequest,
  useUpdateModule,
} from "src/api/workspacesV2Api";
import { Button } from "src/base-components/Button";
import { Input } from "src/base-components/Input";
import { WorkspaceDb } from "src/clients/flow-api";
import { OrganizationInDB } from "src/clients/taktile-api";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { logger } from "src/utils/logger";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  workspace: WorkspaceDb;
  organization: OrganizationInDB;
  moduleType: string;
  module: ModuleSpec;
  afterLeave?: () => void;
};

type FormData = {
  config?: Record<string, string>;
  version: string;
};

const deserializeConfigValueToString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const serializeConfigValueToJsonable = (
  stringValue: string,
  originalValue: unknown,
): unknown => {
  if (stringValue === "") {
    return "";
  }

  if (originalValue === null || originalValue === undefined) {
    return stringValue;
  }

  const originalType = typeof originalValue;

  if (originalType === "boolean") {
    if (stringValue.toLowerCase() === "true") {
      return true;
    }
    if (stringValue.toLowerCase() === "false") {
      return false;
    }

    throw new Error("Invalid value for boolean input");
  }

  if (originalType === "number") {
    const parsed = Number(stringValue);
    return isNaN(parsed) ? stringValue : parsed;
  }

  if (originalType === "object") {
    try {
      return JSON.parse(stringValue);
    } catch {
      return stringValue;
    }
  }

  return stringValue;
};

const getStatusClasses = (status: string) => {
  if (status.toLowerCase().includes("complete")) {
    return "text-green-700 bg-green-100";
  }
  if (status.toLowerCase().includes("progress")) {
    return "text-yellow-700 bg-yellow-100";
  }
  if (status.toLowerCase().includes("failed")) {
    return "text-red-700 bg-red-100";
  }
  return "text-gray-700 bg-gray-100";
};

const getInputSuffixIcon = (isEmptyDirty: boolean, isChanged: boolean) => {
  if (isEmptyDirty) {
    return {
      icon: faExclamationTriangle,
      variant: "danger" as const,
    };
  }
  if (isChanged) {
    return {
      icon: faPen,
    };
  }
  return undefined;
};

const getButtonText = (
  isSubmitting: boolean,
  hasChanges: boolean,
  changeCount: number,
) => {
  if (isSubmitting) {
    return "Saving...";
  }
  if (hasChanges) {
    return `Save ${changeCount} Change${changeCount !== 1 ? "s" : ""}`;
  }
  return "No Changes";
};

export const ModuleConfigModal: React.FC<Props> = ({
  isOpen,
  onClose,
  workspace,
  organization,
  moduleType,
  module,
  afterLeave,
}) => {
  const updateModule = useUpdateModule();

  const {
    handleSubmit,
    formState: { isSubmitting, dirtyFields },
    reset,
    control,
  } = useForm<FormData>({
    values: {
      config: Object.entries(module.config).reduce(
        (acc, [key, value]) => {
          acc[key] = deserializeConfigValueToString(value);
          return acc;
        },
        {} as Record<string, string>,
      ),
      version: module.version,
    },
  });

  const watchedVersion = useWatch({ name: "version", control });
  const watchedConfig = useWatch({ name: "config", control });

  const configKeys = Object.keys(module.config);

  // Track which fields have changed using dirtyFields
  const getChangedFields = () => {
    const changes: Array<
      | { key: string; oldValue: string; newValue: string; type: "config" }
      | { oldValue: string; newValue: string; type: "version" }
    > = [];

    if (dirtyFields.version) {
      const currentVersion = watchedVersion;
      if (currentVersion !== module.version) {
        changes.push({
          oldValue: module.version,
          newValue: currentVersion,
          type: "version",
        });
      }
    }

    if (dirtyFields.config) {
      const currentValues = watchedConfig || {};

      Object.keys(dirtyFields.config).forEach((key) => {
        const configRecord = module.config as Record<string, unknown>;
        const originalValue = configRecord?.[key]
          ? deserializeConfigValueToString(configRecord[key])
          : "";
        const newValue = currentValues[key] || "";

        if (newValue !== originalValue) {
          changes.push({
            key,
            oldValue: originalValue,
            newValue,
            type: "config",
          });
        }
      });
    }

    return changes;
  };

  // Get empty/dirty config fields for validation display
  const getEmptyDirtyConfigFields = () => {
    const emptyFields: string[] = [];

    if (dirtyFields.config) {
      const currentValues = watchedConfig || {};

      Object.keys(dirtyFields.config).forEach((key) => {
        const newValue = currentValues[key] || "";
        if (newValue === "") {
          emptyFields.push(key);
        }
      });
    }

    return emptyFields;
  };

  const changedFields = getChangedFields();
  const hasChanges = changedFields.length > 0;
  const emptyDirtyConfigFields = getEmptyDirtyConfigFields();
  const hasEmptyDirtyConfigFields = emptyDirtyConfigFields.length > 0;
  const hasEmptyDirtyVersionField =
    dirtyFields.version! && watchedVersion === "";
  const isVersionChanged =
    dirtyFields.version! && watchedVersion !== module.version;

  const onSubmit = async (data: FormData) => {
    try {
      const updateData: ModuleUpdateRequest = {};

      if (dirtyFields.config && data.config !== undefined) {
        const configRecord = module.config as Record<string, unknown>;
        const serializedConfig = Object.keys(dirtyFields.config).reduce(
          (acc, key) => {
            const stringValue = data.config?.[key];
            const originalValue = configRecord?.[key];
            acc[key] = serializeConfigValueToJsonable(
              stringValue || "",
              originalValue,
            );
            return acc;
          },
          {} as Record<string, unknown>,
        );

        if (Object.keys(serializedConfig).length > 0) {
          updateData.config = serializedConfig;
        }
      }

      if (dirtyFields.version && data.version !== module.version) {
        updateData.version = data.version;
      }

      await updateModule.mutateAsync({
        workspaceId: workspace.id,
        moduleType,
        data: updateData,
      });

      toastActions.success({
        title: "Configuration updated",
        description: `Module ${getModuleDisplayName(
          moduleType,
        )} has been updated successfully.`,
      });
      onClose();
    } catch (error) {
      const rawErrorMessage =
        axios.isAxiosError(error) && error.response?.data?.detail
          ? error.response.data.detail
          : error;

      const errorDescription =
        typeof rawErrorMessage == "string"
          ? rawErrorMessage
          : JSON.stringify(rawErrorMessage);

      logger.error(`Error when updating module: ${error}`);
      toastActions.failure({
        title: "Update failed",
        description: errorDescription,
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      afterLeave={afterLeave}
      open={isOpen}
      size="lg"
      onClose={handleClose}
    >
      <Modal.Header>
        Configure {getModuleDisplayName(moduleType)}
        <p className="mt-2 font-inter-normal-13px text-gray-600">
          Update configuration parameters for <strong>{workspace.name}</strong>
        </p>
      </Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="space-y-6">
            {/* Module Info Card */}
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-4 font-inter-semibold-13px text-gray-900">
                Module Details
              </h3>

              <div className="grid grid-cols-2 gap-4 font-inter-normal-13px">
                {/* Left Column */}
                <div className="space-y-3">
                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Organization
                    </div>
                    <a
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      href={`/admin-panel/organizations?search=${organization.id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {organization.name}
                    </a>
                    <div className="font-code-12 text-gray-500">
                      ID: {organization.id}
                    </div>
                  </div>

                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Workspace
                    </div>
                    <a
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      href={`/admin-panel/organizations?search=${workspace.id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {workspace.name}
                    </a>
                    <div className="font-code-12 text-gray-500">
                      Slug: {workspace.slug}
                    </div>
                    <div className="font-code-12 text-gray-500">
                      ID: {workspace.id}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-3">
                  <div>
                    <div className="mb-2 font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Version
                    </div>
                    <div className="rounded border bg-white p-3">
                      <Controller
                        control={control}
                        name="version"
                        render={({ field: controllerField }) => (
                          <Tooltip
                            disabled={!hasEmptyDirtyVersionField}
                            title="Value cannot be empty"
                            asChild
                          >
                            <Input
                              {...controllerField}
                              errored={hasEmptyDirtyVersionField}
                              placeholder="Enter version"
                              suffixIcon={getInputSuffixIcon(
                                hasEmptyDirtyVersionField,
                                isVersionChanged,
                              )}
                              fullWidth
                            />
                          </Tooltip>
                        )}
                      />
                      <div className="mt-2 font-inter-normal-13px text-gray-600">
                        <span className="text-gray-500">Current: </span>
                        <code className="rounded border bg-gray-100 px-2 py-1 font-code-12">
                          {module.version}
                        </code>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="font-inter-medium-12px text-gray-500 uppercase tracking-wide">
                      Status
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-inter-medium-12px ${getStatusClasses(
                        module.status,
                      )}`}
                    >
                      {module.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration Fields */}
            {configKeys.length > 0 ? (
              <div className="space-y-5">
                <div className="border-gray-200 border-b pb-2">
                  <h3 className="font-inter-semibold-16px text-gray-900">
                    Configuration Parameters
                  </h3>
                  <p className="mt-1 font-inter-normal-13px text-gray-500">
                    Modify the configuration values for this module
                  </p>
                </div>

                {configKeys.map((key) => {
                  const originalValue = deserializeConfigValueToString(
                    (module.config as Record<string, unknown>)[key] ?? "",
                  );
                  const updatedValue = watchedConfig?.[key] || "";
                  const isChanged =
                    (dirtyFields.config?.[key] &&
                      updatedValue !== String(originalValue)) ||
                    false;
                  const isEmptyDirty =
                    (dirtyFields.config?.[key] && updatedValue === "") || false;

                  return (
                    <div
                      key={key}
                      className="rounded-lg border border-gray-200 bg-white p-4"
                    >
                      <label
                        className="mb-3 block font-inter-medium-13px text-gray-900"
                        htmlFor={`config.${key}`}
                      >
                        <code className="rounded-md border px-2.5 py-1 font-code-13">
                          {key}
                        </code>
                      </label>
                      <Controller
                        control={control}
                        name={`config.${key}` as const}
                        render={({ field: controllerField }) => (
                          <Tooltip
                            disabled={!isEmptyDirty}
                            title="Value cannot be empty"
                            asChild
                          >
                            <Input
                              {...controllerField}
                              errored={isEmptyDirty}
                              placeholder={`Enter value for ${key}`}
                              suffixIcon={getInputSuffixIcon(
                                isEmptyDirty,
                                isChanged,
                              )}
                              fullWidth
                            />
                          </Tooltip>
                        )}
                      />
                      {originalValue && (
                        <div className="mt-2 font-inter-normal-13px text-gray-600">
                          <span className="text-gray-500">Current: </span>
                          <code className="rounded border bg-gray-100 px-2 py-1 font-code-12">
                            {originalValue}
                          </code>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                <p>No configuration parameters found for this module.</p>
              </div>
            )}

            {/* Changes Summary */}
            {hasChanges && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                <h3 className="mb-3 font-inter-semibold-13px text-orange-900">
                  📝 Summary of Changes ({changedFields.length})
                </h3>
                <div className="space-y-2">
                  {changedFields.map((change) => (
                    <div
                      key={
                        change.type === "version"
                          ? "version"
                          : `config-${change.key}`
                      }
                      className="font-inter-normal-13px"
                    >
                      <code className="rounded bg-orange-100 px-2 py-1 font-code-12 text-orange-800">
                        {change.type === "version" ? "Version" : change.key}
                      </code>
                      <div className="mt-1 ml-1 font-inter-normal-12px text-orange-700">
                        <span className="text-gray-500 line-through">
                          {change.oldValue || "(empty)"}
                        </span>
                        <span className="mx-2">→</span>
                        <span className="font-medium">{change.newValue}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal.Content>

        <Modal.Footer
          primaryButton={
            <Button
              disabled={
                isSubmitting ||
                !hasChanges ||
                hasEmptyDirtyConfigFields ||
                hasEmptyDirtyVersionField
              }
              htmlType="submit"
              loading={isSubmitting}
              variant="primary"
            >
              {getButtonText(isSubmitting, hasChanges, changedFields.length)}
            </Button>
          }
          secondaryButton="Cancel"
        />
      </form>
    </Modal>
  );
};
